// Création rapide de zone (POST /zones) — champs minimaux, sans communes obligatoires

import React, { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../lib/axios";

function apiErrorMessage(err: unknown, fallback: string): string {
  const ax = err as {
    response?: { data?: { error?: string | { code?: string; message?: string } } };
  };
  const d = ax.response?.data?.error;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && "message" in d && typeof d.message === "string") {
    return d.message;
  }
  return fallback;
}

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
};

export default function ModalZoneQuickCreate({
  open,
  onClose,
  onCreated,
}: Props): React.JSX.Element | null {
  const [nom, setNom] = useState("");
  const [code, setCode] = useState("");
  const [type, setType] = useState("");
  const [actif, setActif] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setNom("");
      setCode("");
      setType("");
      setActif(true);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nom.trim()) {
      toast.error("Le nom de la zone est obligatoire.");
      return;
    }

    setLoading(true);
    try {
      await api.post("/zones", {
        nom: nom.trim(),
        code: code.trim() || null,
        type: type.trim() || null,
        actif,
      });
      toast.success("Zone créée.");
      onCreated();
      onClose();
    } catch (err: unknown) {
      if (
        (err as { response?: { status?: number } }).response?.status === 409
      ) {
        toast.error(apiErrorMessage(err, "Une zone avec ce nom existe déjà."));
      } else {
        toast.error(apiErrorMessage(err, "Erreur lors de la création de la zone."));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div
        className="relative max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-zone-title"
      >
        <button
          type="button"
          className="absolute right-4 top-4 rounded-lg p-1 text-slate-400 hover:bg-slate-800 hover:text-white"
          onClick={onClose}
          aria-label="Fermer"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 id="modal-zone-title" className="text-lg font-bold text-white">
          Créer une zone
        </h2>
        <p className="mt-1 text-sm text-slate-400">
          Définissez au minimum le nom. Vous pourrez affiner province et communes plus tard si besoin.
        </p>

        <form onSubmit={(e) => void handleSubmit(e)} className="mt-6 space-y-4">
          <label className="block text-sm">
            <span className="text-slate-400">Nom *</span>
            <input
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-emerald-500/30"
              required
            />
          </label>

          <label className="block text-sm">
            <span className="text-slate-400">Code</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-emerald-500/30"
            />
          </label>

          <label className="block text-sm">
            <span className="text-slate-400">Type</span>
            <input
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-emerald-500/30"
              placeholder="Ex. urbain, périphérie…"
            />
          </label>

          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={actif}
              onChange={(e) => setActif(e.target.checked)}
              className="rounded border-slate-600"
            />
            Zone active
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-600 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Créer la zone
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
